/**
 * GuestHome — /event/:slug/guest — the logged-in-guest companion to
 * GuestLanding (/e/:slug). Shares the same package-aware tab engine
 * (lib/features.js) so the two entry points behave identically, then
 * layers on the guest-specific MegaMissions + SquadCrown modules.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Microphone, Lightning, CaretRight, UploadSimple, BookOpen, FilmStrip, Play, ImageSquare } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import MegaMissions from '../components/MegaMissions';
import SquadCrown from '../components/SquadCrown';
import { toast } from 'sonner';
import { buildTabs, FEATURE_TAB_MAP, TAB_VALUE_LABEL, FEATURE_CARDS, activateFeature, pickNewPipFeature, markFeatureSeen, markFeatureComplete, completionStats, useEngagement, engagementCounterText } from '../lib/features';
import { EventLivePills } from '../components/EventLivePills';
import { ProductCard } from '../components/ProductCard';
import { HeroCTA } from '../components/HeroCTA';
import CompletionBar from '../components/CompletionBar';
import CompletionShareSheet from '../components/CompletionShareSheet';
import SupplierBrowseLayer, { SoftMarketplaceCTA } from '../components/SupplierBrowseLayer';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import { fetchOnce } from '../lib/eventCache';
import ReconnectingScreen from '../components/ReconnectingScreen';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAB_META = {
  upload:     { label: 'Upload',     Icon: UploadSimple },
  guestbook:  { label: 'Leave a memory',  Icon: BookOpen },
  video:      { label: 'Video',      Icon: Microphone },
  booth:      { label: 'Booth',      Icon: Camera },
  challenges: { label: 'Challenges', Icon: Lightning },
  highlights: { label: 'Highlights', Icon: FilmStrip },
};

const ICON_MAP = { BookOpen, Microphone, Camera, Lightning, Play, FilmStrip, ImageSquare };

const GuestHome = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { event: recoveredEvent, status: recoveryStatus, retry: retryRecovery } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  const [event, setEvent] = useState(null);
  const notFound = recoveryStatus === 'failed';
  const [moments, setMoments] = useState(0);
  const [liveGuestCount, setLiveGuestCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const tabs = useMemo(() => (event ? buildTabs(event.features) : ['upload']), [event]);
  const [activeTab, setActiveTab] = useState('upload');
  const activatedRef = useRef(new Set());
  const { counts: engagementCounts } = useEngagement(event?.event_id, axios, API);
  const [browseOpen, setBrowseOpen] = useState(() => searchParams.get('browse') === '1');
  useEffect(() => { setBrowseOpen(searchParams.get('browse') === '1'); }, [searchParams]);
  const openBrowse = () => { const n = new URLSearchParams(searchParams); n.set('browse', '1'); setSearchParams(n); };
  const closeBrowse = () => { const n = new URLSearchParams(searchParams); n.delete('browse'); setSearchParams(n); };

  useEffect(() => {
    if (recoveryStatus !== 'ready' || !recoveredEvent?.event_id) return;
    setEvent(recoveredEvent);
    const eid = recoveredEvent.event_id;
    // fetchOnce — shares the prefetcher's in-flight promise OR returns
    // the 60s-fresh cached value. Eliminates duplicate round-trips.
    fetchOnce(`${eid}:photos`, () => axios.get(`${API}/events/${eid}/photos`).then((r) => r.data))
      .then((photos) => setMoments(Array.isArray(photos) ? photos.length : 0))
      .catch(() => {});
    fetchOnce(`${eid}:public-stats`, () => axios.get(`${API}/events/${eid}/public-stats`).then((r) => r.data), { ttl: 30_000 })
      .then((stats) => setLiveGuestCount(Number(stats?.guests || 0)))
      .catch(() => {});
  }, [recoveryStatus, recoveredEvent]);

  // Fire activate-feature whenever a non-upload tab is opened. Dedup
  // via the page-session Set so re-taps don't hit the API.
  useEffect(() => {
    if (!event?.event_id || !event?.features?.length) return;
    const wanted = event.features.filter((f) => FEATURE_TAB_MAP[f] === activeTab);
    wanted.forEach((f) => activateFeature(axios, API, event.event_id, f, activatedRef.current));
  }, [activeTab, event?.event_id, event?.features]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !event) return;
    setUploading(true);
    let ok = 0, fail = 0;
    try {
      const CONCURRENCY = 3;
      for (let i = 0; i < files.length; i += CONCURRENCY) {
        const batch = files.slice(i, i + CONCURRENCY);
        const results = await Promise.allSettled(batch.map(file => {
          const fd = new FormData(); fd.append('file', file);
          return axios.post(`${API}/events/${event.event_id}/photos?source=qr`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
          });
        }));
        results.forEach(r => { if (r.status === 'fulfilled') ok++; else fail++; });
      }
      for (let i = 0; i < ok; i++) {
        window.dispatchEvent(new CustomEvent('eventdrop:photo_upload'));
      }
      if (ok && !fail) toast.success(ok === 1 ? 'Memory saved!' : `${ok} memories saved!`);
      else if (ok && fail) toast.warning(`${ok} uploaded, ${fail} failed — retry?`);
      else toast.error('Upload failed — please try again');
      setMoments(p => p + ok);
      if (ok) {
        const c = markFeatureComplete(slug, 'upload');
        if (!c.already) {
          try {
            window.dispatchEvent(new CustomEvent('eventdrop:completion_tick'));
            if ((event?.features || []).length > 1) {
              toast.success(`✨ ${c.count} experience${c.count === 1 ? '' : 's'} unlocked`, { duration: 2200 });
            }
          } catch {}
        }
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (notFound) return <ReconnectingScreen status="failed" onRetry={retryRecovery} dashboardHref="/" slug={slug} activeEvent={activeEvent} />;
  if (!event) return <ReconnectingScreen status={recoveryStatus === 'restoring' ? 'restoring' : 'reconnecting'} slug={slug} activeEvent={activeEvent} />;

  return (
    <div className="min-h-screen px-5 py-8" style={{ background: 'radial-gradient(circle at top, #1a1a2e, #15161B)' }} data-testid="guest-home">
      {/* Universal back nav — top-left safe area, cinematic glass pill. */}
      <div className="max-w-md mx-auto mb-4">
        <BackButton fallbackHref={`/event/${slug}`} label="Back" variant="subtle" testId="guest-home-back-btn" />
      </div>
      {/* Header */}
      <div className="text-center mb-6">
        <EventDropLogo size={36} className="mx-auto mb-3 opacity-70" />
        <h1 className="text-2xl font-black text-white tracking-tight mb-1" data-testid="guest-event-name">{event.event_name}</h1>
        {event.event_date && <p className="text-white/40 text-sm">{event.event_date}</p>}
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#15161B] border border-white/10">
          <span className="text-[#B89762] font-bold">{moments}</span>
          <span className="text-white/40 text-sm">moments captured</span>
        </div>
      </div>

      {/* COMPLETION BAR — driven by lib/features completion store. */}
      <CompletionBar
        slug={slug}
        features={event.features || []}
        onTabHint={(tab) => setActiveTab(tab)}
      />

      {/* PEAK-MOMENT SHARE TRIGGER — fires once when fully completed. */}
      <CompletionShareSheet
        slug={slug}
        features={event.features || []}
        eventName={event.event_name}
        eventId={event.event_id}
      />

      {/* LIVE CONTRIBUTION COUNTER — immediate social proof on arrival. */}
      {liveGuestCount >= 2 && (
        <div className="max-w-md mx-auto mb-4 text-center" data-testid="guest-home-activity-banner">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black border" style={{ background: 'rgba(255, 107, 53, 0.08)', color: '#FFB88A', borderColor: 'rgba(255, 107, 53, 0.25)' }}>
            🔥 <span className="tabular-nums">{liveGuestCount > 99 ? '99+' : liveGuestCount}</span>
            {` guests already added memories`}
          </span>
        </div>
      )}

      {/* Experience value strip */}
      {tabs.length > 1 && (
        <div className="max-w-md mx-auto mb-5 text-center" data-testid="guest-home-experience-strip">
          <p className="text-[10px] font-black tracking-[2.5px] uppercase text-[#C29D52]/85 mb-1.5">✨ This event includes</p>
          <p className="text-white/60 text-xs">
            {tabs.map((t) => TAB_VALUE_LABEL[t]).filter(Boolean).join(' · ')}
          </p>
        </div>
      )}

      {/* PRIMARY UPLOAD — the most important action on the dashboard.
          Refactored 2026-05-12 to a gold-glow glass HeroCTA matching
          the ProductCard aesthetic below — sits inside the same dark
          cinematic visual language, just wider and gold-tier. The
          spec retains its priority per the user brief ("Most important
          CTA should remain Upload Moment. This can stay wider/larger
          than the rest"). */}
      <div className="max-w-md mx-auto mb-5" data-testid="guest-home-panel-upload">
        <HeroCTA
          testId="guest-upload-btn"
          title="Share your moments"
          subtitle="Add a moment to tonight"
          icon={UploadSimple}
          accent="gold"
          busy={uploading}
          busyLabel="Uploading…"
          onClick={() => fileRef.current?.click()}
        />
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleUpload} />
      </div>

      {/* PRODUCT GRID — premium product-card system (May 2026 refactor).
          Per the brief, replaces the stacked gradient pill launchers
          with a modular 2-col grid of dark glass cards. Reads as a
          cinematic experience catalogue, not a feature menu. */}
      {event.features && event.features.length > 0 && (
        <div className="max-w-md mx-auto mb-6" data-testid="guest-home-feature-launchers">
          <EventLivePills counts={engagementCounts} testId="guest-home-live-pills" />
          <h3
            data-testid="guest-home-do-header"
            className="text-white/95 mb-3"
            style={{
              fontSize: 22, fontWeight: 400,
              letterSpacing: '-0.02em',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}
          >
            Tonight's experiences
          </h3>
          <div
            className="grid"
            style={{
              gap: 14,
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              alignItems: 'stretch',  // equal-height cards in each row
            }}
          >
          {(() => {
            // Cap NEW pip to a single card per page per the brand-doc
            // rule (§5.1 — "max 1-2 visible at once, only for genuinely
            // new launch surfaces"). Prefer the most-recently-launched
            // eligible feature so newly-shipped products (Memory Snap)
            // reliably win the pip over older HIGH_VALUE entries.
            const pipWinner = pickNewPipFeature(slug, event.features || []);
            return event.features
              .filter((f) => FEATURE_CARDS[f])
              .sort((a, b) => {
                const ai = FEATURE_TAB_MAP[a] ? ['upload', 'guestbook', 'video', 'spin360', 'memory_snap', 'booth', 'challenges', 'highlights'].indexOf(FEATURE_TAB_MAP[a]) : 99;
                const bi = FEATURE_TAB_MAP[b] ? ['upload', 'guestbook', 'video', 'spin360', 'memory_snap', 'booth', 'challenges', 'highlights'].indexOf(FEATURE_TAB_MAP[b]) : 99;
                return ai - bi;
              })
              .map((feature) => {
                const card = FEATURE_CARDS[feature];
                const Icon = ICON_MAP[card.iconName] || Camera;
                const showPip = feature === pipWinner;
                const counterText = engagementCounterText(feature, engagementCounts);
                return (
                  <ProductCard
                    key={feature}
                    testId={`guest-home-launch-${feature}`}
                    title={card.title}
                    subtitle={card.subtitle || card.copy}
                    icon={Icon}
                    accent={card.accent || 'gold'}
                    meta={counterText}
                    featured={showPip}
                    onClick={() => {
                      markFeatureSeen(slug, feature);
                      activateFeature(axios, API, event.event_id, feature, activatedRef.current);
                      navigate(card.route(slug));
                    }}
                  />
                );
              });
          })()}
          </div>
        </div>
      )}

      {/* Upload-tab content (missions, squad crown, marketplace) always visible */}
      <div className="max-w-md mx-auto">
        {/* Mega Missions — Experience/Legacy guests get timed quests with badges */}
        <div className="mb-6"><MegaMissions event={event} /></div>

        {/* Squad Crown — end-of-night announcement hijacks the whole screen */}
        <SquadCrown event={event} />

        {/* GUEST → MARKETPLACE BRIDGE — warm moment, low commitment. */}
        {completionStats(slug, event.features || []).completed >= 1 && (
          <SoftMarketplaceCTA eventId={event.event_id} onOpen={openBrowse} testid="guest-home-marketplace-cta" />
        )}

        {/* Live hint */}
        <div className="rounded-2xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-white/30 text-sm">New moments are being added right now</p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-white/15 text-xs">Powered by EventDrop</p>
      </div>

      {/* Supplier browse layer — slide-up marketplace bridge. */}
      <SupplierBrowseLayer isOpen={browseOpen} onClose={closeBrowse} event={event} />
    </div>
  );
};

export default GuestHome;
